import { Route, RouteComponentProps, Switch} from 'react-router';
import { connect } from 'react-redux';
import { ApplicationState } from '../../store';
import { ToxnotComponentProperties } from '../core/BaseApplicationComponents/ToxnotComponent';
import { LoadingSpinner, LoadingSpinnerSize } from '../core/LoadingSpinner';
import * as CustomerStateController from '../../store/StateControllers/Customers/CustomersStateController';
import { TableColumn, TableRow, ToxnotReactTable } from '../core/ToxnotReactTable';
import { MyCustomersProps } from './MyCustomersCommon';
import MyCustomerInfo from './MyCustomerInfo';
import MyCustomerPurchases from './MyCustomerPurchases';
import { ToxnotReactComponentPaths } from '../core/ToxnotReactComponentPaths';
import MyCustomerRequestDetail from './MyCustomerRequestDetail';
import { SelectedInventoriesManager } from '../../store/Managers/SelectedInventoriesManager';
import { nullOrEmptyCoalesce } from '../../utilities/Utilities';
import Upsell, { UpsellTypes } from '../Upsell/Upsell';
import { UpsellProBadge } from '../core/Upsell';
import MyCustomerRequests from './MyCustomerRequests';
import { ToxnotSignInRequiredComponent } from '../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import './MyCustomers.css';

export enum ActiveTabType {
    Requests = "Requests",
    Purchases = "Purchases",
    Info = "Info"
}

interface MyCustomerDetailState {
    activeTab: ActiveTabType;   
}

type MyCustomerDetailProps =
    MyCustomersProps &
    RouteComponentProps<{ customerId: string }>;
class MyCustomerDetail extends ToxnotSignInRequiredComponent<MyCustomerDetailProps, MyCustomerDetailState> {

    constructor(props: MyCustomerDetailProps) {
       
        super(props);

        this.state = {
            activeTab: ActiveTabType.Requests
        }
    }

// ===============================================================================

    public componentDidMount() {
        this.setState({
            activeTab: this.getInitialActiveTab()
        });


        const customerId = this.props.match.params.customerId;

        this.props.loadCustomerIntoCurrentlySelected(customerId);
    }

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // ===============================================================================

    public componentWillUnmount() {
        this.props.loadCustomerIntoCurrentlySelected(null);
        this.props.setRequestIdForModal(null);
    }

    // ===============================================================================

    protected renderConcreteComponent() {

        if (this.props.currentlySelectedCustomerId === null) {
            return (
                <div className="text-center">
                    {<LoadingSpinner size={LoadingSpinnerSize.Medium} />}
                </div>
            );
        }

        const customer = this.props.customerIdToCustomerTable[this.props.currentlySelectedCustomerId];
       
        if (this.props.sharedMaterialCompanies.length === 0) {
          
            return <div className="text-center"><p>To access this page, you need to have at least one brand associated with your account.</p>
                <p>Please visit the <a href="Exchange" id="passportLibraryLink"><b>Passport Library</b></a> tab and click on 'My Brands' to add or view your brands.</p>                           </div>;
        }
        return (
            <div id="customer-detail-root" className="tn-section">
                <div className="tn-section-row tn-section-heading-row">
                    <div className="tn-section-col-12">
                        <h1><button id="customer-detail-back-button" type="button" className="btn btn-back" onClick={this.backButton_click}><span className="glyphicon glyphicon-chevron-left"></span></button> {(nullOrEmptyCoalesce(customer.companyName, this.localizeString("Unnamed Company")))}</h1>
                    </div>
                </div>
                <Switch>
                    <Route exact={true} path={ToxnotReactComponentPaths.MyCustomerDetailPath}>
                        <div className="tn-section-row">
                            <div className="tn-section-col-12">
                                <div className="tn-tabs">
                                    <ul className="nav nav-tabs">
                                        <li role="presentation" onClick={e => this.setActiveTab_click(ActiveTabType.Requests)} className={this.getClassNameForTab(this.state.activeTab === ActiveTabType.Requests)}>
                                            <a href="#requests-tab" role="tab" data-toggle="tab">{this.localizeString("Requests")} {!this.getUserContextForSignedInUser().userCapabilities.myCustomersEnabled && <UpsellProBadge />}</a>
                                        </li>
                                        <li role="presentation" onClick={e => this.setActiveTab_click(ActiveTabType.Purchases)} className={this.getClassNameForTab(this.state.activeTab === ActiveTabType.Purchases)}><a href="#purchases-tab" role="tab" data-toggle="tab">{this.localizeString("Parts")}</a></li>
                                        <li role="presentation" onClick={e => this.setActiveTab_click(ActiveTabType.Info)} className={this.getClassNameForTab(this.state.activeTab === ActiveTabType.Info)}><a href="#info-tab" role="tab" data-toggle="tab">{this.localizeString("Customer Name & Contacts")}</a></li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div id="customer-detail-tab-content" className="tab-content">
                            <div role="tabpanel" className={"tab-pane" + this.getClassNameForTab(this.state.activeTab === ActiveTabType.Requests, true)} id="requests-tab">
                                {this.renderRequestsTable(this.props.currentlySelectedCustomerId)}
                            </div>
                            <div role="tabpanel" className={"tab-pane" + this.getClassNameForTab(this.state.activeTab === ActiveTabType.Purchases, true)} id="purchases-tab">
                                <MyCustomerPurchases />
                            </div>
                            <div role="tabpanel" className={"tab-pane" + this.getClassNameForTab(this.state.activeTab === ActiveTabType.Info, true)} id="info-tab">
                                <MyCustomerInfo />
                            </div>
                        </div>
                    </Route>
                    <Route exact={true} path={ToxnotReactComponentPaths.MyCustomerDetailRequestDetailPath} component={MyCustomerRequestDetail} />

                </Switch>
            </div>
        );
    }

    // ===============================================================================

    private renderRequestsTable(customerId: string){
        let body: JSX.Element;

        if (!this.getUserContextForSignedInUser().userCapabilities.myCustomersEnabled || this.props.customerIdsToRequests === null) {
            body = <Upsell upsellType={UpsellTypes.Pro} renderEmbedded={true} conversionEvent="my-customers" />
        }

        else {
            body = <MyCustomerRequests />
        }

        return body;
    }

    // ===============================================================================

    private getInitialActiveTab() {
        const localStorageVal = localStorage.getItem("customer-last-active-tab");

        if (localStorageVal !== null) {
            localStorage.removeItem("customer-last-active-tab");
            return localStorageVal as ActiveTabType;
        }

        return this.getUserContextForSignedInUser().userCapabilities.myCustomersEnabled ? ActiveTabType.Requests : ActiveTabType.Purchases;
    }

    // ===============================================================================

    private getClassNameForTab(isActive: boolean, addSpace?: boolean) {
        return isActive ? (addSpace ? " active" : "active") : "";
    }

    // ===============================================================================

    //
    // UI Events
    //

    private backButton_click = () => {
        if (this.props.currentlySelectedCustomerId && this.props.location.pathname.includes("/Request/")) {
            this.props.history.push("/MyCustomers/Browse/Customer/" + this.props.currentlySelectedCustomerId);
            return;
        }
        if (this.props.currentlySelectedCustomerId && this.props.location.pathname === ("/MyCustomers/Browse/Customer/" + this.props.currentlySelectedCustomerId)) {
            this.props.history.push("/MyCustomers/Browse/");
            return;
        }
        this.props.history.goBack();
    }

    // ===============================================================================

    private setActiveTab_click = (type: ActiveTabType) => {
        this.setState({
            activeTab: type
        });
    }
}

export default connect(
    (state: ApplicationState) => ({ appServices: state.appServices, ...state.myCustomersState }),
    CustomerStateController.actionCreators
)(MyCustomerDetail as any);
